import React, { useEffect, useMemo, type ReactNode } from 'react';
import { useRibbon } from '../../context/RibbonContext';
import { MasterRibbon } from '../MasterRibbon';
import type { RibbonConfig } from '../../types/ribbon';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface ModuleLayoutProps {
  ribbon?: ReactNode | null;
  ribbonConfig?: RibbonConfig | null;
  headerMeta?: ReactNode | null;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function ModuleLayout({
  ribbon,
  ribbonConfig,
  headerMeta,
  children,
  className = '',
  contentClassName = '',
}: ModuleLayoutProps) {
  const { setRibbonContent, setHeaderMetaContent } = useRibbon();
  const isMobile = useIsMobile();
  const isViewportLock = className.includes('mobile-viewport-lock');

  const ribbonNode = useMemo(() => {
    if (ribbon) return ribbon;
    if (ribbonConfig) return <MasterRibbon config={ribbonConfig} />;
    return null;
  }, [ribbon, ribbonConfig]);

  useEffect(() => {
    setRibbonContent(ribbonNode);
  }, [ribbonNode, setRibbonContent]);

  useEffect(() => () => setRibbonContent(null), [setRibbonContent]);

  useEffect(() => {
    setHeaderMetaContent(headerMeta ?? null);
  }, [headerMeta, setHeaderMetaContent]);

  useEffect(() => () => setHeaderMetaContent(null), [setHeaderMetaContent]);

  const shellClass = isViewportLock || !isMobile
    ? 'flex h-full min-h-0 flex-col overflow-hidden'
    : 'flex min-h-0 flex-col';

  const contentClass = isViewportLock || !isMobile
    ? 'min-h-0 flex-1 overflow-x-hidden max-w-full'
    : 'min-h-0 flex-1 max-w-full overflow-x-hidden overflow-y-visible';

  return (
    <div className={`${shellClass} ${className}`.trim()}>
      <div className={`${contentClass} ${contentClassName}`.trim()}>{children}</div>
    </div>
  );
}

export default ModuleLayout;
